import { Injectable } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';

type FormValidators = Array<(control: AbstractControl) => ValidationErrors | null>;

@Injectable({
	providedIn: 'root',
})
export class CartingConfigViewModel {
	public isLoadingConfiguration = false;
	public isUpdatingConfiguration = false;

	// Reactive form for general config
	public form: FormGroup;

	// Forms for priority and toggles
	public groupSortingPriorityForm: FormGroup;
	public componentSortingPriorityForm: FormGroup;

	public formFieldsLookup: { property: string; control: AbstractControl }[] = [];
	public groupSortingFormFieldsLookup: { property: string; control: AbstractControl }[] = [];
	public componentSortingFormFieldsLookup: { property: string; control: AbstractControl }[] = [];

	// General setting
	public maxProductionSequenceNumberJumpsInSameWagon: number | null = null;
	public splitDoorAndWindowSashes: boolean = false;
	public groupByColors: boolean = false;

	public productionRoute: boolean | null = null;
	public paintInside: boolean | null = null;
	public profileSet: boolean | null = null;
	public profileSetGroup: boolean | null = null;
	public geometry: boolean | null = null;

	// Sorting flags and priorities
	public groupsortingProductionSequenceNumber = false;
	public groupsortingProductionSequenceNumberOrder = false;
	public groupsortingProductionSequenceNumberPriority: number | null = null;

	public componentSortingFrameField = false;
	public componentSortingFrameFieldOrder = false;
	public componentSortingFrameFieldPriority: number | null = null;

	public componentSortingHeight = false;
	public componentSortingHeightOrder = false;
	public componentSortingHeightPriority: number | null = null;

	public componentSortingWidth = false;
	public componentSortingWidthOrder = false;
	public componentSortingWidthPriority: number | null = null;

	public componentSortingSurfaceArea = false;
	public componentSortingSurfaceAreaOrder = false;
	public componentSortingSurfaceAreaPriority: number | null = null;

	constructor(private fb: FormBuilder) {
		this.initializeForms();
		this.setFormFieldsLookupMaps();
	}

	private get formValidators(): FormValidators {
		return [Validators.min(0), Validators.max(1000)];
	}

	private get priorityValidators(): FormValidators {
		return [this.notUnique.bind(this), Validators.max(2_147_483_647)];
	}

	private initializeForms(): void {
		this.form = this.fb.group({
			maxProductionSequenceNumberJumpsInSameWagon: [this.maxProductionSequenceNumberJumpsInSameWagon, this.formValidators],
			splitDoorAndWindowSashes: [this.splitDoorAndWindowSashes],
			groupByColors: [this.groupByColors],
			productionRoute: [this.productionRoute],
			paintInside: [this.paintInside, this.formValidators],
			profileSet: [this.profileSet, this.formValidators],
			profileSetGroup: [this.profileSetGroup, this.formValidators],
			geometry: [this.geometry, this.formValidators],
		});

		this.groupSortingPriorityForm = this.fb.group({
			groupsortingProductionSequenceNumberPriority: [this.groupsortingProductionSequenceNumberPriority, this.priorityValidators],
		});

		this.componentSortingPriorityForm = this.fb.group({
			componentSortingFrameFieldPriority: [this.componentSortingFrameFieldPriority, this.priorityValidators],
			componentSortingHeightPriority: [this.componentSortingHeightPriority, this.priorityValidators],
			componentSortingWidthPriority: [this.componentSortingWidthPriority, this.priorityValidators],
			componentSortingSurfaceAreaPriority: [this.componentSortingSurfaceAreaPriority, this.priorityValidators],
		});
	}

	private setFormFieldsLookupMaps(): void {
		this.formFieldsLookup = [
			{
				property: 'maxProductionSequenceNumberJumpsInSameWagon',
				control: this.form.get('maxProductionSequenceNumberJumpsInSameWagon'),
			},
			{
				property: 'splitDoorAndWindowSashes',
				control: this.form.get('splitDoorAndWindowSashes'),
			},
			{ property: 'groupByColors', control: this.form.get('groupByColors') },
			{ property: 'productionRoute', control: this.form.get('productionRoute') },
			{ property: 'paintInside', control: this.form.get('paintInside') },
			{ property: 'profileSet', control: this.form.get('profileSet') },
			{ property: 'profileSetGroup', control: this.form.get('profileSetGroup') },
			{ property: 'geometry', control: this.form.get('geometry') },
		];

		this.groupSortingFormFieldsLookup = [
			{
				property: 'groupsortingProductionSequenceNumber',
				control: this.groupSortingPriorityForm.get('groupsortingProductionSequenceNumberPriority'),
			},
		];

		this.componentSortingFormFieldsLookup = [
			{
				property: 'componentSortingFrameField',
				control: this.componentSortingPriorityForm.get('componentSortingFrameFieldPriority'),
			},
			{
				property: 'componentSortingHeight',
				control: this.componentSortingPriorityForm.get('componentSortingHeightPriority'),
			},
			{
				property: 'componentSortingWidth',
				control: this.componentSortingPriorityForm.get('componentSortingWidthPriority'),
			},
			{
				property: 'componentSortingSurfaceArea',
				control: this.componentSortingPriorityForm.get('componentSortingSurfaceAreaPriority'),
			},
		];
	}

	public get controls() {
		return this.form.controls;
	}

	public get groupSortingPriorityControls() {
		return this.groupSortingPriorityForm.controls;
	}

	public get componentSortingPriorityControls() {
		return this.componentSortingPriorityForm.controls;
	}

	public clear(): void {
		this.isLoadingConfiguration = false;
		this.isUpdatingConfiguration = false;

		this.form.reset();
		this.groupSortingPriorityForm.reset();
		this.componentSortingPriorityForm.reset();
		// clear all properties to their initial state
		this.productionRoute = null;
		this.paintInside = null;
		this.profileSet = null;
		this.geometry = null;
		this.maxProductionSequenceNumberJumpsInSameWagon = null;
		this.splitDoorAndWindowSashes = false;
		this.groupByColors = false;
		this.groupsortingProductionSequenceNumber = false;
		this.groupsortingProductionSequenceNumberOrder = false;
		this.groupsortingProductionSequenceNumberPriority = null;
		this.componentSortingFrameField = false;
		this.componentSortingFrameFieldOrder = false;
		this.componentSortingFrameFieldPriority = null;
		this.componentSortingHeight = false;
		this.componentSortingHeightOrder = false;
		this.componentSortingHeightPriority = null;
		this.componentSortingWidth = false;
		this.componentSortingWidthOrder = false;
		this.componentSortingWidthPriority = null;
		this.componentSortingSurfaceArea = false;
		this.componentSortingSurfaceAreaOrder = false;
		this.componentSortingSurfaceAreaPriority = null;
	}

	private notUnique(target: FormControl): { error: string } | null {
		const form = target.parent as FormGroup;
		if (!target.value) return null;

		const targetValue = String(target.value);
		for (const [_, control] of Object.entries(form.controls)) {
			if (control !== target && String(control.value) === targetValue) {
				return { error: 'notUnique' };
			}
		}
		return null;
	}

	public setGroupSortingFromServer(params: any[]): void {
		for (const param of params) {
			if (param.type.self === '/carting/config-group-sorting-strategies/productionSequenceNumber') {
				this.groupsortingProductionSequenceNumber = true;
				this.groupsortingProductionSequenceNumberOrder = param.order === 0;
				this.groupsortingProductionSequenceNumberPriority = param.priority ?? null;
			}
		}
	}

	public setComponentSortingFromServer(params: any[]): void {
		for (const param of params) {
			const order = param.order === 0;
			const priority = param.priority ?? null;

			switch (param.type.self) {
				case '/carting/config-component-sorting-strategies/frame-field':
					this.componentSortingFrameField = true;
					this.componentSortingFrameFieldOrder = order;
					this.componentSortingFrameFieldPriority = priority;
					break;
				case '/carting/config-component-sorting-strategies/height':
					this.componentSortingHeight = true;
					this.componentSortingHeightOrder = order;
					this.componentSortingHeightPriority = priority;
					break;
				case '/carting/config-component-sorting-strategies/width':
					this.componentSortingWidth = true;
					this.componentSortingWidthOrder = order;
					this.componentSortingWidthPriority = priority;
					break;
				case '/carting/config-component-sorting-strategies/surface-area':
					this.componentSortingSurfaceArea = true;
					this.componentSortingSurfaceAreaOrder = order;
					this.componentSortingSurfaceAreaPriority = priority;
					break;
			}
		}
	}

	public nullifyDisabledSortingStrategies(): void {
		if (!this.groupsortingProductionSequenceNumber) {
			this.groupsortingProductionSequenceNumberOrder = false;
			this.groupsortingProductionSequenceNumberPriority = null;
		}
		if (!this.componentSortingFrameField) {
			this.componentSortingFrameFieldOrder = false;
			this.componentSortingFrameFieldPriority = null;
		}
		if (!this.componentSortingHeight) {
			this.componentSortingHeightOrder = false;
			this.componentSortingHeightPriority = null;
		}
		if (!this.componentSortingWidth) {
			this.componentSortingWidthOrder = false;
			this.componentSortingWidthPriority = null;
		}
		if (!this.componentSortingSurfaceArea) {
			this.componentSortingSurfaceAreaOrder = false;
			this.componentSortingSurfaceAreaPriority = null;
		}
	}

	public getGroupSortingFromViewModel(): any[] {
		const result: any[] = [];
		if (this.groupsortingProductionSequenceNumber) {
			result.push({
				order: this.groupsortingProductionSequenceNumberOrder ? 0 : 1,
				priority: Number(this.groupsortingProductionSequenceNumberPriority),
				type: { self: '/carting/config-group-sorting-strategies/productionSequenceNumber' },
			});
		}
		return result;
	}

	public getComponentSortingFromViewModel(): any[] {
		const result: any[] = [];

		if (this.componentSortingFrameField) {
			result.push({
				order: this.componentSortingFrameFieldOrder ? 0 : 1,
				priority: Number(this.componentSortingFrameFieldPriority),
				type: { self: '/carting/config-component-sorting-strategies/frame-field' },
			});
		}

		if (this.componentSortingHeight) {
			result.push({
				order: this.componentSortingHeightOrder ? 0 : 1,
				priority: Number(this.componentSortingHeightPriority),
				type: { self: '/carting/config-component-sorting-strategies/height' },
			});
		}

		if (this.componentSortingWidth) {
			result.push({
				order: this.componentSortingWidthOrder ? 0 : 1,
				priority: Number(this.componentSortingWidthPriority),
				type: { self: '/carting/config-component-sorting-strategies/width' },
			});
		}

		if (this.componentSortingSurfaceArea) {
			result.push({
				order: this.componentSortingSurfaceAreaOrder ? 0 : 1,
				priority: Number(this.componentSortingSurfaceAreaPriority),
				type: { self: '/carting/config-component-sorting-strategies/surface-area' },
			});
		}

		return result;
	}

	private get isGroupSortingFormValid(): boolean {
		return this.groupSortingFormFieldsLookup.every(({ control }) => !control.enabled || control.valid);
	}

	private get isComponentSortingFormValid(): boolean {
		return this.componentSortingFormFieldsLookup.every(({ control }) => !control.enabled || control.valid);
	}

	public get areFormsValid(): boolean {
		return this.form.valid && this.isGroupSortingFormValid && this.isComponentSortingFormValid;
	}

	public get isSaveButtonDisabled(): boolean {
		return !this.areFormsValid || this.isUpdatingConfiguration;
	}

	public get isResetButtonDisabled(): boolean {
		return this.isUpdatingConfiguration;
	}
}
